import { createFileRoute } from "@tanstack/react-router";
import { Phone, MapPin, Clock } from "lucide-react";
import { PageBanner } from "@/components/layout/PageBanner";
import { Button } from "@/components/ui/button";

const CONTACT_TITLE = "Contact Homestead Construction | Searcy AR";
const CONTACT_DESC =
  "Free consultation for home remodeling, additions, and custom projects in Searcy, Arkansas. Call 501-593-6361.";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: CONTACT_TITLE },
      { name: "description", content: CONTACT_DESC },
      { property: "og:title", content: CONTACT_TITLE },
      { property: "og:description", content: CONTACT_DESC },
      { property: "og:url", content: "https://homesteadconstruction.us/contact" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: CONTACT_TITLE },
      { name: "twitter:description", content: CONTACT_DESC },
    ],
    links: [{ rel: "canonical", href: "https://homesteadconstruction.us/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Homestead Construction",
          url: "https://homesteadconstruction.us/contact",
          mainEntity: {
            "@type": "GeneralContractor",
            name: "Homestead Construction of NEA",
            telephone: "+1-501-593-6361",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Searcy",
              addressRegion: "AR",
              addressCountry: "US",
            },
            openingHours: "Mo-Fr 07:00-17:00",
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <>
      <PageBanner title="Contact Us" />

      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-2xl mx-auto text-center">
          <p className="eyebrow">Contact us</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-ink leading-tight mt-3">
            Let's Talk — Give Us a Call
          </h2>
          <p className="mt-5 text-ink/70 font-body text-lg">
            We'd love to hear from you. The best way to get started is simple: pick up the phone
            and call. We genuinely enjoy talking through your ideas, answering your questions, and
            brainstorming what's possible for your home. No forms, no waiting on email — just a
            real conversation. Call us today and let's build something together.
          </p>

          <div className="mt-8 flex justify-center">
            <Button asChild variant="brand" size="xl">
              <a href="tel:5015936361">
                <Phone className="h-4 w-4" /> Call Us
              </a>
            </Button>
          </div>

          <ul className="mt-12 space-y-6 text-left inline-block mx-auto">
            <li className="flex gap-4">
              <span className="grid place-content-center h-12 w-12 rounded-full bg-brand text-white shrink-0">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-semibold uppercase text-ink">Location</p>
                <p className="text-ink/70">Searcy, Arkansas USA</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="grid place-content-center h-12 w-12 rounded-full bg-brand text-white shrink-0">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-semibold uppercase text-ink">Phone</p>
                <a href="tel:5015936361" className="text-ink/70 hover:text-brand">
                  501-593-6361
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="grid place-content-center h-12 w-12 rounded-full bg-brand text-white shrink-0">
                <Clock className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-semibold uppercase text-ink">Hours</p>
                <p className="text-ink/70">Monday–Friday 7am to 5pm</p>
                <p className="text-ink/70">Closed Sunday</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section className="w-full h-[400px]">
        <iframe
          title="Map of Searcy, Arkansas"
          src="https://www.google.com/maps?q=Searcy,Arkansas&output=embed"
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </>
  );
}
